import { ACTION_ITEM_DISMISSED_EVENTS, ACTION_ITEM_EVENTS } from './action-items.js';
import { getBasePropsSchema, CLIENT_SURFACES } from './base-props.js';
export { APP_BASE_PROPS, BASE_PROP_GROUPS, SERVER_BASE_PROPS, groupBaseProps } from './base-props.js';
import { CORPORATE_EVENTS } from './corporate.js';
import { EVENT_DESTINATION } from './destinations.js';
export { CLIENT_AD_EVENT_DESTINATIONS, DEALER_DATALAYER_DESTINATIONS, SERVER_AD_EVENT_DESTINATIONS } from './destinations.js';
import { ENGAGEMENT_EVENTS } from './engagement.js';
import { EXPRESS_EVENTS } from './express.js';
import { FUNNEL_EVENTS } from './funnel.js';
export { EXPRESS_GATE_DECIDED_OUTCOME, EXPRESS_GATE_OUTCOME } from './funnel.js';
import { OPERATIONAL_EVENTS } from './operational.js';
import { SIGNING_EVENTS } from './signing.js';
export { ECOMMERCE_PAYLOAD } from './shared-fields.js';

/**
 * Canonical event registry—GA4 snake_case as the internal standard and the
 * single source of truth for every analytics event name in the codebase.
 *
 * Events are organized into seven files by semantic grouping:
 *
 * - `funnel.ts`—ecommerce funnel stages, conversion outcomes, and their
 *   internal-only companions. Events with `CLIENT_AD_FANOUT` in `destinations`
 *   fan out to ad-platform pixels via ConversionTracking; events with
 *   `SERVER_AD_FANOUT` fan out to ad platforms server-side via Pub/Sub.
 * - `engagement.ts`—product interactions that aren't funnel steps (listing
 *   views, searches, filter usage, modal opens, configurator interactions).
 * - `operational.ts`—backend state transitions (financing status, order
 *   fulfillment, insurance, payments, title & registration) and system
 *   performance metrics.
 * - `corporate.ts`—ekho.com marketing-site events, captured CDP-only
 *   (PostHog + Segment); the site's GTM container owns its own ad fan-out.
 * - `express.ts`—the Express prequal/finance checkout flow's funnel, step,
 *   field, and engagement events, captured CDP-only.
 * - `signing.ts`—the vendor-neutral SigningService lifecycle, captured CDP-only.
 * - `action-items.ts`—facts observed from action-item transitions by the generic
 *   Pub/Sub tracking route, named for the domain that moved rather than for the
 *   action-item mechanism that carried it, plus one derived `_dismissed` event
 *   per registered `_pending` transition.
 *
 * Callsites must reference `EKHO_EVENTS.<KEY>`—never a hardcoded event name
 * string. The emitted (wire) name is resolved via `getEventName`. Every event
 * lands in the Ekho-internal CDP (Segment → BigQuery + PostHog) unconditionally;
 * `destinations` adds only the extra fan-out legs on top of that baseline.
 */
const EKHO_EVENTS = {
    ...FUNNEL_EVENTS,
    ...ENGAGEMENT_EVENTS,
    ...OPERATIONAL_EVENTS,
    ...CORPORATE_EVENTS,
    ...EXPRESS_EVENTS,
    ...SIGNING_EVENTS,
    ...ACTION_ITEM_EVENTS,
    ...ACTION_ITEM_DISMISSED_EVENTS,
};
/** The one accessor for an event's emitted (wire) name. */
function getEventName(event) {
    return event.wireName ?? event.canonical;
}
/** Whether the event fans out to `destination`. Absent `destinations` is CDP-only. */
/**
 * Whether an event reaches a given CDP sink. Both are on unless the event's
 * `cdp` block turns one off, so an event that declares nothing is unaffected.
 *
 * Every dispatcher asks this rather than reading the field directly, because
 * the two surfaces do not share one: the shared web adapter serves the widget
 * and the apps, and the SDK carries its own. A helper is what keeps the
 * default from drifting between them.
 */
/**
 * How an event's CDP routing reads in generated docs and the debug logger.
 * Both render from this rather than from `destinations`, which describes the
 * additional fan-out legs and says nothing about the two CDP sinks. An event
 * that opts one out is no longer accurately described as "CDP-only".
 */
function describeCdpSinks(event) {
    const posthog = sendsToCdpSink(event, 'posthog');
    const segment = sendsToCdpSink(event, 'segment');
    if (posthog && segment)
        return 'PostHog + Segment';
    if (posthog)
        return 'PostHog only';
    if (segment)
        return 'Segment only';
    return 'none';
}
function sendsToCdpSink(event, sink) {
    return event.cdp?.[sink] !== false;
}
function hasDestination(event, destination) {
    return event.destinations?.includes(destination) ?? false;
}
/** Whether the event fans out to ad platforms client-side via ConversionTracking. */
function isClientAdFanoutEvent(event) {
    return hasDestination(event, EVENT_DESTINATION.ClientAdFanout);
}
/**
 * The name for the web adapter's plain dealer-dataLayer push, or `undefined`
 * when no push should happen. Ad-fanout events return `undefined`—their
 * dataLayer leg is owned by ConversionTracking, and a second plain push would
 * double-fire the dealer's GTM triggers.
 */
function getDealerDataLayerName(event) {
    if (!hasDestination(event, EVENT_DESTINATION.GtmDataLayer))
        return undefined;
    if (isClientAdFanoutEvent(event))
        return undefined;
    return event.adEventNames?.gtm;
}
/** Look up an event definition by its canonical GA4 name. */
function getEventByCanonical(canonical) {
    return Object.values(EKHO_EVENTS).find((e) => e.canonical === canonical);
}
/**
 * The full payload an event emits on `surface`—its caller-provided `schema`
 * composed with the base props the adapter injects on that surface.
 *
 * This is the accessor the runtime validator and the generated manifest both
 * read, so a registry entry never restates base props and the two can never
 * describe different payloads.
 *
 * `surface` is where the event actually fires, which is the emitting adapter's
 * `ekhoApp`—not the entry's own `surface` field, since an event declared for two
 * surfaces emits a different payload on each.
 */
function getEventPayloadSchema(event, surface) {
    const base = getBasePropsSchema(surface);
    return event.schema ? base.merge(event.schema) : base;
}
/**
 * Every surface an event may emit from, for manifest expansion. Falls back to
 * every client surface when an entry declares no `surface`, since the registry
 * does not constrain where an undeclared event can be tracked from.
 */
function getEventSurfaces(event) {
    const { surface } = event;
    if (!surface)
        return CLIENT_SURFACES;
    return typeof surface === 'string' ? [surface] : surface;
}

export { CLIENT_SURFACES, EKHO_EVENTS, EVENT_DESTINATION, describeCdpSinks, getBasePropsSchema, getDealerDataLayerName, getEventByCanonical, getEventName, getEventPayloadSchema, getEventSurfaces, hasDestination, isClientAdFanoutEvent, sendsToCdpSink };
//# sourceMappingURL=index.js.map
